// src/viqai/WorkflowBoard.jsx
// ─── Workflow (§30-32 / §13, §44 du cahier 2) ──────────────────────────────
//
// Trois corrections, toutes visibles à l'écran :
//
//   1. §30, §44 — Mobile. Quatre colonnes côte à côte sur 390 px donnent
//      quatre bandes de 90 px, illisibles et vides. Sous 760 px, on passe en
//      onglets : une colonne à la fois, cartes verticales pleine largeur. Le
//      kanban à quatre colonnes reste le mode desktop.
//
//   2. §26 cahier 2 — L'erreur. « La tâche n'a pas pu être créée. » était
//      affichée quelle que soit la cause. Le backend renvoie maintenant un
//      message précis (échéance illisible, schéma, contrainte) : on l'affiche
//      tel quel, avec le détail de debug quand il est fourni.
//
//   3. §29, §32 — L'agenda ne bloque plus, et les cartes portent leurs actions
//      métier : appeler, ouvrir le lead, ouvrir le dossier, valider.

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { apiGet, apiPost, apiPatch } from "./api";

const COLUMNS = [
  ["todo", "À faire"],
  ["in_progress", "En cours"],
  ["review", "À valider"],
  ["done", "Validé"],
];
const PRIORITY_LABEL = { urgent: "Urgent", high: "Prioritaire" };
const ACTION_LABEL = { task: "Tâche", call: "Appel", rdv: "RDV", sourcing: "Sourcing", publish: "Publier", followup: "Relance" };

function useIsMobile(breakpoint = 760) {
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const on = (e) => setMobile(e.matches);
    setMobile(mq.matches);
    // Safari < 14 n'a pas addEventListener sur MediaQueryList.
    if (mq.addEventListener) { mq.addEventListener("change", on); return () => mq.removeEventListener("change", on); }
    mq.addListener(on);
    return () => mq.removeListener(on);
  }, [breakpoint]);
  return mobile;
}

function dueLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso), now = new Date();
  if (isNaN(d)) return null;
  const sameDay = d.toDateString() === now.toDateString();
  if (d < now && !sameDay) return { text: `En retard · ${d.toLocaleDateString("fr-FR")}`, tone: "neg" };
  if (sameDay) return { text: `Aujourd'hui ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`, tone: "warn" };
  return { text: d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }), tone: null };
}

// §32 — Une carte doit permettre d'agir, pas seulement d'être déplacée.
function initialOf(name) { return String(name || "?").trim().charAt(0).toUpperCase() || "?"; }
function assigneeLabel(task) { return task?.assignee_name || task?.assignee_email || "Collaborateur"; }

const memberUid = m => String(m?.uid || m?.user_uid || "").trim();

function TaskCard({ task, onMove, onOpenDossier, onOpenLead, onOpenAgenda, onOpenNote, onCall, onAssign, onProposeEdit, onApproveChange, onRejectChange, onArchive, onUnarchive, canManageProcesses, canArchiveDone, members, processes, dossiers, leads, agendaItems, notes, dragging, setDragging, mobile }) {
  const [editing, setEditing] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const draftFromTask = (t) => ({
    title:t.title||"", description:t.description||"", due_at:t.due_at?String(t.due_at).slice(0,16):"", priority:t.priority||"normal",
    process_id:t.process_id?String(t.process_id):"", dossier_id:t.dossier_id?String(t.dossier_id):"", lead_id:t.lead_id?String(t.lead_id):"", agenda_id:t.agenda_id?String(t.agenda_id):"", note_id:t.note_id?String(t.note_id):"",
  });
  const [editDraft, setEditDraft] = useState(() => draftFromTask(task));
  const [editBusy, setEditBusy] = useState(false);
  const due = dueLabel(task.due_at);
  const context = task.dossier_label?.trim() || task.lead_client || task.agenda_label || task.note_label || null;
  const idx = COLUMNS.findIndex(c => c[0] === task.status);
  const phone = task.lead_phone || null;

  return (<>
    <div className={`wf-card${dragging === task.id ? " dragging" : ""}${task.archived_at ? " archived" : ""}`}
      draggable={!mobile && !task.archived_at}
      onDragStart={e => { setDragging(task.id); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(task.id)); }}
      onDragEnd={() => setDragging(null)}>

      <button type="button" className="wf-card-title wf-card-title-button" onClick={()=>setDetailOpen(true)} title="Afficher la tâche">{task.title}</button>
      {task.process_name && <div className={`wf-process-badge ${task.process_color_key||"copper"}`}>{task.process_name}</div>}
      {task.archived_at && <div className="wf-archive-badge">✓ Archivée · {new Date(task.archived_at).toLocaleDateString("fr-FR")}</div>}
      {task.pending_change && !task.archived_at && <div className="wf-change-badge">Modification en attente de validation</div>}
      {task.action_type && task.action_type !== "task" && <div className={`wf-action-tag ${task.action_type}`}>{ACTION_LABEL[task.action_type] || task.action_type}</div>}
      {context && <div className="wf-card-context">{context}</div>}
      {task.description && <div className="wf-card-desc">{task.description}</div>}

      <div className="wf-card-foot">
        {due && <span className={`wf-due${due.tone ? " " + due.tone : ""}`}>{due.text}</span>}
        {PRIORITY_LABEL[task.priority] && (
          <span className={`ws-badge${task.priority === "urgent" ? " danger" : " warning"}`}>{PRIORITY_LABEL[task.priority]}</span>
        )}
        {task.assignee_uid && <span className="wf-assignee-inline" title={`Assigné à ${assigneeLabel(task)}`}><span className="wf-assignee-bubble">{initialOf(assigneeLabel(task))}</span><span>{assigneeLabel(task)}</span></span>}
      </div>

      <div className="wf-card-actions">
        <button type="button" className="ws-btn sm" onClick={() => setDetailOpen(true)}>Afficher</button>
        {phone && <button type="button" className="ws-btn sm" onClick={() => onCall(task)}>Appeler</button>}
        {task.lead_id && <button type="button" className="ws-btn sm" onClick={() => onOpenLead(task)}>Ouvrir le lead</button>}
        {task.dossier_id && <button type="button" className="ws-btn sm" onClick={() => onOpenDossier(task)}>Ouvrir le dossier</button>}
        {task.agenda_id && <button type="button" className="ws-btn sm" onClick={() => onOpenAgenda(task)}>Ouvrir le RDV</button>}
        {task.note_id && <button type="button" className="ws-btn sm" onClick={() => onOpenNote(task)}>Ouvrir la note</button>}
        {!task.archived_at && <button type="button" className="ws-btn sm" onClick={() => { setEditDraft(draftFromTask(task)); setEditing(v=>!v); }}>Modifier</button>}
        {!task.archived_at && task.status !== "done" && (
          <button type="button" className="ws-btn sm secondary" onClick={() => onMove(task, "done")}>Valider</button>
        )}
        {!task.archived_at && task.status === "done" && (task.is_process_manager || canArchiveDone) && (
          <button type="button" className="ws-btn sm secondary" onClick={()=>onArchive(task)}>Archiver</button>
        )}
        {task.archived_at && (task.is_process_manager || canArchiveDone) && (
          <button type="button" className="ws-btn sm secondary" onClick={()=>onUnarchive(task)}>Restaurer</button>
        )}
        {!task.archived_at && task.pending_change && (task.is_process_manager || canManageProcesses) && <><button type="button" className="ws-btn sm primary" onClick={()=>onApproveChange(task)}>Valider la modification</button><button type="button" className="ws-btn sm" onClick={()=>onRejectChange(task)}>Refuser</button></>}
        {!task.archived_at && !!members?.length && <label className="wf-assign-control"><span>Assigné à</span><select className="wf-transfer" value={task.assignee_uid || ""} aria-label="Assigner cette tâche"
          onChange={e=>onAssign(task,e.target.value)}>
          <option value="">Moi</option>
          {members.filter(m=>memberUid(m)).map(m=><option key={memberUid(m)} value={memberUid(m)}>{m.name || m.email || "Collaborateur"}</option>)}
        </select></label>}
      </div>

      {editing && !task.archived_at && <div className="wf-edit-box">
        <label>Titre<input value={editDraft.title} onChange={e=>setEditDraft(v=>({...v,title:e.target.value}))}/></label>
        <label>Description<textarea rows={3} value={editDraft.description} onChange={e=>setEditDraft(v=>({...v,description:e.target.value}))}/></label>
        <div className="wf-edit-row"><label>Échéance<input type="datetime-local" value={editDraft.due_at} onChange={e=>setEditDraft(v=>({...v,due_at:e.target.value}))}/></label><label>Priorité<select value={editDraft.priority} onChange={e=>setEditDraft(v=>({...v,priority:e.target.value}))}><option value="low">Basse</option><option value="normal">Normale</option><option value="high">Prioritaire</option><option value="urgent">Urgent</option></select></label></div>
        <div className="wf-edit-links">
          <label>Processus<select value={editDraft.process_id} onChange={e=>setEditDraft(v=>({...v,process_id:e.target.value}))}><option value="">Général / auto</option>{(processes||[]).filter(p=>p.active).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select></label>
          <label>Dossier<select value={editDraft.dossier_id} onChange={e=>setEditDraft(v=>({...v,dossier_id:e.target.value}))}><option value="">Aucun</option>{(dossiers||[]).map(d=><option key={d.id} value={d.id}>{[d.make||d.marque,d.model||d.modele,d.plaque].filter(Boolean).join(" ")||`Dossier #${d.id}`}</option>)}</select></label>
          <label>Lead<select value={editDraft.lead_id} onChange={e=>setEditDraft(v=>({...v,lead_id:e.target.value}))}><option value="">Aucun</option>{(leads||[]).map(l=><option key={l.id} value={l.id}>{l.client_name||l.client||l.email||`Lead #${l.id}`}</option>)}</select></label>
          <label>RDV<select value={editDraft.agenda_id} onChange={e=>setEditDraft(v=>({...v,agenda_id:e.target.value}))}><option value="">Aucun</option>{(agendaItems||[]).filter(a=>!a.external).map(a=><option key={a.id} value={a.id}>{a.title||`RDV #${a.id}`}</option>)}</select></label>
          <label>Note<select value={editDraft.note_id} onChange={e=>setEditDraft(v=>({...v,note_id:e.target.value}))}><option value="">Aucune</option>{(notes||[]).map(n=><option key={n.id} value={n.id}>{n.title||`Note #${n.id}`}</option>)}</select></label>
        </div>
        <div className="wf-edit-actions"><button className="ws-btn sm primary" disabled={editBusy||!editDraft.title.trim()} onClick={async()=>{setEditBusy(true);const ok=await onProposeEdit(task,editDraft);setEditBusy(false);if(ok)setEditing(false);}}>{editBusy?"Envoi…":"Soumettre la modification"}</button><button className="ws-btn sm" onClick={()=>setEditing(false)}>Annuler</button></div>
        <small>Les changements de contenu sont soumis au responsable du processus. Le statut et l’assignation restent modifiables immédiatement.</small>
      </div>}

      {/* Déplacement au clavier : le glisser-déposer seul exclut les
          utilisateurs qui n'utilisent pas la souris, et n'existe pas au doigt. */}
      {!task.archived_at && <div className="wf-card-nav">
        <button type="button" className="ws-btn ghost sm" disabled={idx <= 0}
          onClick={() => onMove(task, COLUMNS[idx - 1][0])} aria-label="Colonne précédente">←</button>
        <span className="wf-card-status">{COLUMNS[idx]?.[1]}</span>
        <button type="button" className="ws-btn ghost sm" disabled={idx >= COLUMNS.length - 1}
          onClick={() => onMove(task, COLUMNS[idx + 1][0])} aria-label="Colonne suivante">→</button>
      </div>}
    </div>
    {detailOpen && <div className="ws-overlay wf-task-detail-overlay" role="presentation" onMouseDown={e=>e.target===e.currentTarget&&setDetailOpen(false)}>
      <section className="ws-modal wf-task-detail-modal" role="dialog" aria-modal="true" aria-label={`Tâche : ${task.title}`}>
        <header className="ws-modal-head"><div><div className="ws-eyebrow">Workflow · {COLUMNS[idx]?.[1] || "Tâche"}</div><h2>{task.title}</h2><p>{context || "Action VehicleIQ"}</p></div><button type="button" className="ws-icon-btn" onClick={()=>setDetailOpen(false)}>✕</button></header>
        <div className="wf-task-detail-body">
          <div className="wf-task-detail-meta">
            {task.process_name&&<span className={`wf-process-badge ${task.process_color_key||"copper"}`}>{task.process_name}</span>}
            {task.action_type&&<span className={`wf-action-tag ${task.action_type}`}>{ACTION_LABEL[task.action_type]||task.action_type}</span>}
            {due&&<span className={`wf-due${due.tone?" "+due.tone:""}`}>{due.text}</span>}
            {PRIORITY_LABEL[task.priority]&&<span className={`ws-badge${task.priority==="urgent"?" danger":" warning"}`}>{PRIORITY_LABEL[task.priority]}</span>}
            {task.assignee_uid&&<span className="wf-assignee-inline"><span className="wf-assignee-bubble">{initialOf(assigneeLabel(task))}</span><span>{assigneeLabel(task)}</span></span>}
          </div>
          <div className="wf-task-detail-description">{task.description || "Aucune description complémentaire."}</div>
          <div className="wf-task-detail-links">
            {task.lead_id&&<button type="button" className="ws-btn" onClick={()=>{setDetailOpen(false);onOpenLead(task)}}>Ouvrir le lead</button>}
            {task.dossier_id&&<button type="button" className="ws-btn" onClick={()=>{setDetailOpen(false);onOpenDossier(task)}}>Ouvrir le dossier</button>}
            {task.agenda_id&&<button type="button" className="ws-btn" onClick={()=>{setDetailOpen(false);onOpenAgenda(task)}}>Ouvrir le RDV</button>}
            {task.note_id&&<button type="button" className="ws-btn" onClick={()=>{setDetailOpen(false);onOpenNote(task)}}>Ouvrir la note</button>}
            {phone&&<button type="button" className="ws-btn" onClick={()=>onCall(task)}>Appeler</button>}
          </div>
          <div className="wf-task-detail-footer">
            {!task.archived_at&&<button type="button" className="ws-btn secondary" onClick={()=>{setDetailOpen(false);setEditDraft(draftFromTask(task));setEditing(true)}}>Modifier</button>}
            {!task.archived_at&&task.status!=="done"&&<button type="button" className="ws-btn primary" onClick={()=>{setDetailOpen(false);onMove(task,"done")}}>Valider la tâche</button>}
            {!task.archived_at&&task.status==="done"&&(task.is_process_manager||canArchiveDone)&&<button type="button" className="ws-btn primary" onClick={()=>{setDetailOpen(false);onArchive(task)}}>Archiver la tâche</button>}
            {task.archived_at&&(task.is_process_manager||canArchiveDone)&&<button type="button" className="ws-btn secondary" onClick={()=>{setDetailOpen(false);onUnarchive(task)}}>Restaurer</button>}
          </div>
        </div>
      </section>
    </div>}
  </>);
}

export default function WorkflowBoard({ onOpenDossier, onOpenLead, onOpenAgenda, onOpenNote, onCountChange, onOpenGoals, allowExpand = true, modalMode = false, compact = false }) {
  const [columns, setColumns] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const [draft, setDraft] = useState({ title: "", due_at: "", priority: "normal", dossier_id: "", lead_id:"", agenda_id:"", note_id:"", process_id:"", action_type: "task", assignee_uid: "" });
  const [creating, setCreating] = useState(false);
  const [dossiers, setDossiers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [agendaItems, setAgendaItems] = useState([]);
  const [notes, setNotes] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [activeProcess, setActiveProcess] = useState("all");
  const [canManageProcesses, setCanManageProcesses] = useState(false);
  const [canArchiveDone, setCanArchiveDone] = useState(false);
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [active, setActive] = useState("todo");
  const mobile = useIsMobile();
  const [composeOpen, setComposeOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [expandedClosing, setExpandedClosing] = useState(false);
  const closeExpanded = () => {
    if (expandedClosing) return;
    setExpandedClosing(true);
    window.setTimeout(() => { setExpanded(false); setExpandedClosing(false); }, 160);
  };

  useEffect(() => {
    // v225 — Le Kanban reste la vue principale : le formulaire de création
    // n'occupe de place que sur demande, sur desktop comme sur mobile.
    if (mobile) setComposeOpen(false);
  }, [mobile]);
  useEffect(() => {
    const openComposer = () => setComposeOpen(true);
    window.addEventListener("viq-workflow-new-task", openComposer);
    return () => window.removeEventListener("viq-workflow-new-task", openComposer);
  }, []);

  const loadInFlight = useRef(false);
  const load = useCallback(async ({ background = false } = {}) => {
    // v219 — Un refresh automatique ne concurrence jamais une session IA.
    // Le premier chargement et les mutations explicites restent prioritaires.
    if (background && typeof window !== "undefined" && window.__viqAiBusy) return;
    if (loadInFlight.current) return;
    loadInFlight.current = true;
    try {
      const j = await apiGet("/api/workflow/tasks").catch(() => null);
      if (j?.success) {
        setColumns(j.columns);
        setProcesses(Array.isArray(j.processes)?j.processes:[]);
        setCanManageProcesses(!!j.can_manage_processes);
        setCanArchiveDone(!!j.can_archive_done);
        setArchivedTasks(Array.isArray(j.archived)?j.archived:[]);
        onCountChange && onCountChange(j.counts?.open || 0);
      } else if (!background) {
        setColumns(c => c || { todo: [], in_progress: [], review: [], done: [] });
        if (j?.error) setError(j.error);
      }
    } finally {
      loadInFlight.current = false;
    }
  }, [onCountChange]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const refresh = () => { if (document.visibilityState !== "hidden") load({ background: true }); };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [load]);
  useEffect(() => {
    const onAiBusy = (e) => { if (e?.detail?.busy === false && document.visibilityState !== "hidden") load({ background: true }); };
    window.addEventListener("viq-ai-busy", onAiBusy);
    return () => window.removeEventListener("viq-ai-busy", onAiBusy);
  }, [load]);
  useEffect(() => {
    const refreshGoals = () => load();
    window.addEventListener("viq-goals-updated", refreshGoals);
    return () => window.removeEventListener("viq-goals-updated", refreshGoals);
  }, [load]);
  useEffect(() => {
    const refreshWorkflow = () => load({ background:true });
    window.addEventListener("viq-workflow-changed", refreshWorkflow);
    return () => window.removeEventListener("viq-workflow-changed", refreshWorkflow);
  }, [load]);
  // Liste courte pour le sélecteur « Dossier lié ». Une tâche rattachée à un
  // véhicule vaut mieux qu'une tâche flottante : elle ouvre le bon dossier.
  useEffect(() => {
    let alive = true;
    apiGet("/api/org/collaboration").then(r=>{ if(alive && r?.success) setMembers(r.members || []); }).catch(()=>{});
    return ()=>{ alive=false; };
  }, []);

  useEffect(() => {
    let alive = true;
    apiGet("/api/dossiers?limit=40")
      .then(r => { if (alive && r?.success) setDossiers((r.dossiers || r.items || []).slice(0, 40)); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    let alive=true; const start=new Date(); start.setDate(start.getDate()-7); const end=new Date(); end.setDate(end.getDate()+60);
    // v255 — /api/workflow/tasks renvoie déjà les processus : on évite la
    // requête doublon /api/workflow/processes au premier rendu. Les données
    // d'enrichissement sont chargées en parallèle sans bloquer le Kanban.
    Promise.all([
      apiGet("/api/pro-leads?limit=50").catch(()=>null),
      apiGet(`/api/agenda/events?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`).catch(()=>null),
      apiGet("/api/crm/notes").catch(()=>null),
    ]).then(([lr,ar,nr])=>{ if(!alive)return; setLeads((lr?.leads||[]).slice(0,50)); setAgendaItems((ar?.events||ar?.items||[]).slice(0,80)); setNotes((nr?.notes||[]).slice(0,80)); });
    return()=>{alive=false;};
  }, []);

  // §13 cahier 2 — Le déplacement persiste. Optimiste pour la fluidité du
  // geste, rechargé si le serveur refuse : un board qui ment sur l'état réel
  // est pire qu'un board lent.
  const move = async (task, status) => {
    if (!status || status === task.status) return;
    const before = columns;
    setColumns(c => {
      const next = { ...c };
      next[task.status] = (next[task.status] || []).filter(t => t.id !== task.id);
      next[status] = [{ ...task, status }, ...(next[status] || [])];
      return next;
    });
    const j = await apiPatch(`/api/workflow/tasks/${task.id}`, { status }).catch(() => null);
    if (!j?.success) {
      setColumns(before);
      setError(j?.error || "Le déplacement n'a pas été enregistré.");
      setTimeout(() => setError(null), 5000);
    } else { window.dispatchEvent(new Event("viq-workflow-changed")); load(); }
  };

  // §1 — Aucun succès affiché avant confirmation du backend. L'ancienne
  // version vidait le formulaire dès l'envoi ; si l'écriture échouait, le
  // professionnel voyait son texte disparaître ET un message d'erreur.
  const create = async () => {
    const title = draft.title.trim();
    if (!title || creating) return;
    setCreating(true); setError(null); setWarning(null);

    const [firstLine, ...rest] = title.split("\n");
    const j = await apiPost("/api/workflow/tasks", {
      title: firstLine.slice(0, 200),
      description: rest.join("\n").trim() || null,
      due_at: draft.due_at || null,
      priority: draft.priority,
      dossier_id: draft.dossier_id || null,
      lead_id: draft.lead_id || null,
      agenda_id: draft.agenda_id || null,
      note_id: draft.note_id || null,
      process_id: draft.process_id || (activeProcess !== "all" ? activeProcess : null),
      action_type: draft.action_type || "task",
      assignee_uid: draft.assignee_uid || null,
    }).catch(e => ({ success: false, error: e?.message }));
    setCreating(false);

    if (!j?.success) {
      // §19 — Le message vient du serveur et reste métier. Le détail SQL, lui,
      // est journalisé côté serveur et repris ici en console uniquement.
      setError(j?.error || "La tâche n'a pas pu être créée.");
      if (j?.debug) console.error("[workflow create]", j.debug);
      return;   // le brouillon est CONSERVÉ : rien n'est perdu
    }

    setDraft({ title: "", due_at: "", priority: "normal", dossier_id: "", lead_id:"", agenda_id:"", note_id:"", process_id: activeProcess!=="all"?activeProcess:"", action_type: "task", assignee_uid: "" });
    if (mobile) setComposeOpen(false);
    if (j.agenda_synced === false) {
      setWarning({ text: "Tâche créée. La synchronisation avec l'agenda n'a pas abouti.", taskId: j.task?.id });
    }
    window.dispatchEvent(new Event("viq-workflow-changed"));
    load();   // §24 — revalidation immédiate après mutation
  };

  const assignTask = async (task, assignee_uid) => {
    setError(null);
    const j = await apiPatch(`/api/workflow/tasks/${task.id}`, { assignee_uid }).catch(()=>null);
    if (!j?.success) { setError(j?.error || "L'assignation n'a pas été enregistrée."); return; }
    window.dispatchEvent(new Event("viq-workflow-changed"));
    await load();
  };

  const proposeEdit = async (task, changes) => {
    setError(null); const j=await apiPost(`/api/workflow/tasks/${task.id}/propose-change`,changes).catch(()=>null);
    if(!j?.success){setError(j?.error||"La modification n'a pas pu être envoyée.");return false;} setWarning(j.pending?{text:"Modification envoyée au responsable du processus pour validation."}:null); window.dispatchEvent(new Event("viq-workflow-changed")); await load(); return true;
  };
  const approveChange = async (task) => { const id=task?.pending_change?.id; if(!id)return; const j=await apiPost(`/api/workflow/changes/${id}/approve`,{}).catch(()=>null); if(!j?.success){setError(j?.error||"Validation impossible.");return;} window.dispatchEvent(new Event("viq-workflow-changed")); await load(); };
  const rejectChange = async (task) => { const id=task?.pending_change?.id; if(!id)return; const j=await apiPost(`/api/workflow/changes/${id}/reject`,{}).catch(()=>null); if(!j?.success){setError(j?.error||"Refus impossible.");return;} window.dispatchEvent(new Event("viq-workflow-changed")); await load(); };
  const archiveTask = async (task) => {
    if(!task?.id)return;
    const j=await apiPost(`/api/workflow/tasks/${task.id}/archive`,{}).catch(()=>null);
    if(!j?.success){setError(j?.error||"Archivage impossible.");return;}
    setWarning({text:"Tâche archivée. Elle reste disponible dans les archives du Workflow."});
    window.dispatchEvent(new Event("viq-workflow-changed")); await load();
  };
  const unarchiveTask = async (task) => {
    if(!task?.id)return;
    const j=await apiPost(`/api/workflow/tasks/${task.id}/unarchive`,{}).catch(()=>null);
    if(!j?.success){setError(j?.error||"Restauration impossible.");return;}
    window.dispatchEvent(new Event("viq-workflow-changed")); await load();
  };

  const retrySync = async (taskId) => {
    const j = await apiPost(`/api/workflow/tasks/${taskId}/sync-agenda`, {}).catch(() => null);
    setWarning(j?.success ? null : { text: "La synchronisation a de nouveau échoué.", taskId });
  };

  const callTask = (task) => {
    if (task.lead_phone) window.location.href = `tel:${String(task.lead_phone).replace(/\s/g, "")}`;
  };

  const visibleColumns = useMemo(() => {
    if(!columns || activeProcess === "all") return columns;
    const pid=Number(activeProcess); return Object.fromEntries(COLUMNS.map(([k])=>[k,(columns[k]||[]).filter(t=>Number(t.process_id)===pid)]));
  },[columns,activeProcess]);
  const total = useMemo(
    () => (visibleColumns ? COLUMNS.reduce((n, [k]) => n + (visibleColumns[k]?.length || 0), 0) : 0),
    [visibleColumns]
  );

  if (!columns) return (
    <div className={`wf-board wf-loading${compact ? " compact" : ""}`} aria-busy="true" aria-label="Chargement du workflow">
      <div className="wf-skeleton-tabs" aria-hidden="true">
        {[70,92,84,110].map((w,i)=><span key={i} style={{width:w}} />)}
      </div>
      <div className="wf-skeleton-actions" aria-hidden="true"><span /><span /></div>
      <div className="wf-skeleton-columns" aria-hidden="true">
        {COLUMNS.map(([key])=><div key={key} className={`wf-skeleton-col wf-col-${key}`}><span className="wf-skeleton-line short"/><span className="wf-skeleton-card"/><span className="wf-skeleton-card small"/></div>)}
      </div>
      <span className="sr-only">Chargement des tâches, processus et assignations…</span>
    </div>
  );

  const cardProps = {
    onMove: move,
    onOpenDossier: (t) => onOpenDossier && onOpenDossier(t.dossier_id),
    onOpenLead: (t) => onOpenLead && onOpenLead(t.lead_id),
    onOpenAgenda: (t) => onOpenAgenda ? onOpenAgenda(t.agenda_id) : window.dispatchEvent(new CustomEvent("viq-open-agenda-item",{detail:{id:t.agenda_id}})),
    onOpenNote: (t) => onOpenNote ? onOpenNote(t.note_id) : window.dispatchEvent(new CustomEvent("viq-open-crm-note",{detail:{id:t.note_id}})),
    onCall: callTask, onAssign: assignTask, onProposeEdit:proposeEdit, onApproveChange:approveChange, onRejectChange:rejectChange,
    onArchive:archiveTask, onUnarchive:unarchiveTask, canManageProcesses, canArchiveDone, members,
    processes, dossiers, leads, agendaItems, notes,
    dragging, setDragging, mobile,
  };

  // ─── §2 (v198) — Zone de saisie pleine largeur ──────────────────────────
  // Le formulaire tenait sur une ligne, à côté de quatre colonnes de kanban :
  // un champ de 240 px pour écrire une instruction de relance. La zone de
  // création est maintenant permanente, occupe TOUTE la largeur du bloc-notes,
  // et son champ principal est un textarea multi-ligne. Le kanban passe
  // dessous.
  const addForm = (
    <section className={`wf-compose${composeOpen ? " open" : " collapsed"}`} aria-label="Nouvelle tâche">
      <button type="button" className="wf-compose-toggle" aria-expanded={composeOpen} onClick={() => setComposeOpen(v => !v)}>
        <span>Nouvelle tâche</span><span aria-hidden="true">{composeOpen ? "−" : "+"}</span>
      </button>
      {composeOpen && <>
      <textarea
        className="ws-textarea wf-compose-input"
        value={draft.title}
        rows={4}
        onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
        onKeyDown={e => {
          // Entrée = nouvelle ligne (c'est un textarea) ; Ctrl/Cmd+Entrée valide.
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); create(); }
          if (e.key === "Escape") { setDraft({ title: "", due_at: "", priority: "normal", dossier_id: "", lead_id:"", agenda_id:"", note_id:"", process_id: activeProcess!=="all"?activeProcess:"", action_type: "task", assignee_uid: "" }); setError(null); }
        }}
        placeholder="Rappeler M. Dupont pour le BMW X5 — proposer une reprise à 18 500 € et vérifier la disponibilité du CT."
      />
      <div className="wf-compose-meta">
        <label className="wf-compose-field">
          <span className="ws-label">Échéance</span>
          <input className="ws-input" type="datetime-local" value={draft.due_at}
            onChange={e => setDraft(d => ({ ...d, due_at: e.target.value }))} />
        </label>
        <label className="wf-compose-field">
          <span className="ws-label">Priorité</span>
          <select className="ws-select" value={draft.priority}
            onChange={e => setDraft(d => ({ ...d, priority: e.target.value }))}>
            <option value="normal">Normale</option>
            <option value="high">Prioritaire</option>
            <option value="urgent">Urgent</option>
            <option value="low">Basse</option>
          </select>
        </label>
        <label className="wf-compose-field wf-compose-action">
          <span className="ws-label">Action</span>
          <select className="ws-select" value={draft.action_type}
            onChange={e => setDraft(d => ({ ...d, action_type: e.target.value }))}>
            <option value="task">Tâche</option>
            <option value="call">Appel</option>
            <option value="rdv">RDV</option>
            <option value="followup">Relance</option>
            <option value="sourcing">Sourcing</option>
            <option value="publish">Publier</option>
          </select>
        </label>
        {processes.filter(p=>p.active).length>0 && <label className="wf-compose-field">
          <span className="ws-label">Processus</span>
          <select className="ws-select" value={draft.process_id || (activeProcess!=="all"?activeProcess:"")} onChange={e=>setDraft(d=>({...d,process_id:e.target.value}))}><option value="">Général / auto</option>{processes.filter(p=>p.active).map(p=><option key={p.id} value={p.id}>{p.name}</option>)}</select>
        </label>}
        <label className="wf-compose-field">
          <span className="ws-label">Assigné à</span>
          <select className="ws-select" value={draft.assignee_uid} onChange={e => setDraft(d => ({ ...d, assignee_uid: e.target.value }))}>
            <option value="">Moi</option>
            {members.filter(m=>memberUid(m)).map(m=><option key={memberUid(m)} value={memberUid(m)}>{m.name || m.email || "Collaborateur"}</option>)}
          </select>
        </label>
        <label className="wf-compose-field">
          <span className="ws-label">Dossier lié</span>
          <select className="ws-select" value={draft.dossier_id}
            onChange={e => setDraft(d => ({ ...d, dossier_id: e.target.value }))}>
            <option value="">Aucun</option>
            {dossiers.map(d => (
              <option key={d.id} value={d.id}>
                {[d.make, d.model, d.plaque].filter(Boolean).join(" ") || `Dossier #${d.id}`}
              </option>
            ))}
          </select>
        </label>        <label className="wf-compose-field"><span className="ws-label">Lead lié</span><select className="ws-select" value={draft.lead_id} onChange={e=>setDraft(d=>({...d,lead_id:e.target.value}))}><option value="">Aucun</option>{leads.map(l=><option key={l.id} value={l.id}>{l.client_name||`Lead #${l.id}`} · {l.type||"demande"}</option>)}</select></label>
        <label className="wf-compose-field"><span className="ws-label">RDV lié</span><select className="ws-select" value={draft.agenda_id} onChange={e=>setDraft(d=>({...d,agenda_id:e.target.value}))}><option value="">Aucun</option>{agendaItems.filter(a=>!a.external).map(a=><option key={a.id} value={a.id}>{a.title||`RDV #${a.id}`}</option>)}</select></label>
        <label className="wf-compose-field"><span className="ws-label">Note liée</span><select className="ws-select" value={draft.note_id} onChange={e=>setDraft(d=>({...d,note_id:e.target.value}))}><option value="">Aucune</option>{notes.map(n=><option key={n.id} value={n.id}>{n.title||`Note #${n.id}`}</option>)}</select></label>
        <div className="wf-compose-submit">
          <button className="ws-btn primary" disabled={!draft.title.trim() || creating} onClick={create}>
            {creating ? "Enregistrement…" : "+ Ajouter"}
          </button>
        </div>
      </div>
      </>}
    </section>
  );

  return (
    <div className={`wf-board${mobile ? " mobile" : ""}${compact ? " compact" : ""}`}>
      {error && <div className="ws-alert error" role="alert">{error}</div>}
      {warning && (
        <div className="ws-alert warning" role="status">
          <span>{warning.text}</span>
          {warning.taskId && (
            <button type="button" className="ws-btn sm" onClick={() => retrySync(warning.taskId)}>
              Réessayer la synchronisation
            </button>
          )}
        </div>
      )}

      <div className="wf-archive-toolbar">
        <button type="button" className={`ws-btn ghost sm${showArchived?" active":""}`} onClick={()=>setShowArchived(v=>!v)}>
          {showArchived ? "Masquer les archives" : `Archives${archivedTasks.length?` (${archivedTasks.length})`:""}`}
        </button>
      </div>
      {showArchived && <section className="wf-archive-panel" aria-label="Tâches archivées">
        <div className="wf-archive-panel-head"><div><span className="ws-eyebrow">Historique Workflow</span><h4>Tâches archivées</h4></div><span>{archivedTasks.length} tâche{archivedTasks.length>1?"s":""}</span></div>
        {archivedTasks.length ? <div className="wf-archive-grid">{archivedTasks.filter(t=>activeProcess==="all"||Number(t.process_id)===Number(activeProcess)).map(t=><TaskCard key={`archive-${t.id}`} task={t} {...cardProps}/>)}</div> : <div className="ws-empty">Aucune tâche archivée.</div>}
      </section>}
      {processes.filter(p=>p.active).length>0 && <div className="wf-process-tabs" role="tablist" aria-label="Processus Workflow"><button type="button" className={activeProcess==="all"?"active":""} onClick={()=>setActiveProcess("all")}>Tous</button>{processes.filter(p=>p.active).map(p=><button type="button" key={p.id} className={`${activeProcess===String(p.id)?"active ":""}${p.color_key||"copper"}`} onClick={()=>{setActiveProcess(String(p.id));setDraft(d=>({...d,process_id:String(p.id)}));}}>{p.name}{p.manager_name?` · ${p.manager_name.split(" ")[0]}`:""}</button>)}</div>}
      {compact && <div className="wf-compact-actions">
        <button type="button" className="ws-btn primary sm" onClick={()=>setComposeOpen(v=>!v)}>{composeOpen ? "− Fermer la création" : "+ Nouvelle tâche"}</button>
        {allowExpand && !modalMode && <button type="button" className="ws-btn ghost sm" onClick={()=>setExpanded(true)}>↗ Agrandir le workflow</button>}
      </div>}
      {(!compact || composeOpen) && addForm}
      {allowExpand && !modalMode && !compact && <div className="wf-board-toolbar"><button type="button" className="ws-btn ghost sm" onClick={()=>setExpanded(true)}>↗ Agrandir le workflow</button></div>}

      {total === 0 && (
        <div className="wf-copilot-starters">
          <h4>VIQ.AI prépare déjà votre prochaine étape</h4>
          <p>Le plan automatique se synchronise avec vos objectifs. En attendant, choisissez le levier à travailler : marge, sourcing, relances ou canaux de vente.</p>
          <div className="wf-copilot-starter-actions">
            <button type="button" className="ws-btn primary sm" onClick={onOpenGoals}>Fixer mes objectifs</button>
            <button type="button" className="ws-btn sm" onClick={() => window.dispatchEvent(new CustomEvent("viq-context-request", { detail:{ prompt:"Analyse mes priorités du moment et propose un plan de performance VO concret pour cette semaine : sourcing, marge, stock, leads, relances et canaux de vente." } }))}>Demander au copilot</button>
          </div>
        </div>
      )}

      {/* §30, §44 — Mobile : onglets, une colonne à la fois. */}
      {mobile ? (
        <>
          <div className="ws-tabs" role="tablist" aria-label="Colonnes du workflow">
            {COLUMNS.map(([key, label]) => (
              <button key={key} type="button" role="tab" className={`ws-tab wf-status-tab wf-status-tab-${key}`}
                aria-selected={active === key} onClick={() => setActive(key)}>
                {label}
                {(visibleColumns[key]?.length || 0) > 0 && <span className="ws-tab-count">{visibleColumns[key].length}</span>}
              </button>
            ))}
          </div>
          <div className={`wf-col-body mobile wf-mobile-status-${active}`}>
            {(visibleColumns[active] || []).map(t => <TaskCard key={t.id} task={t} {...cardProps} />)}
            {!(visibleColumns[active] || []).length && (
              <div className="ws-empty">Rien dans « {COLUMNS.find(c => c[0] === active)?.[1]} ».</div>
            )}
          </div>
        </>
      ) : (
        <div className="wf-columns">
          {COLUMNS.map(([key, label]) => (
            <div key={key}
              className={`wf-col wf-col-${key}${over === key ? " over" : ""}`}
              onDragOver={e => { e.preventDefault(); setOver(key); }}
              onDragLeave={() => setOver(o => (o === key ? null : o))}
              onDrop={e => {
                e.preventDefault(); setOver(null);
                const id = parseInt(e.dataTransfer.getData("text/plain"));
                const task = Object.values(columns).flat().find(t => t.id === id);
                if (task) move(task, key);
                setDragging(null);
              }}>
              <div className="wf-col-head">
                <span>{label}</span>
                <span className="wf-col-team">{[...new Map((visibleColumns[key]||[]).filter(t=>t.assignee_uid).map(t=>[t.assignee_uid,t])).values()].slice(0,4).map(t=><i key={t.assignee_uid} className="wf-assignee-bubble" title={assigneeLabel(t)}>{initialOf(assigneeLabel(t))}</i>)}</span>
                {(visibleColumns[key]?.length || 0) > 0 && <span className="wf-col-count">{visibleColumns[key].length}</span>}
              </div>
              <div className="wf-col-body">
                {(visibleColumns[key] || []).map(t => <TaskCard key={t.id} task={t} {...cardProps} />)}
              </div>
            </div>
          ))}
        </div>
      )}
      {expanded && <div className={`ws-overlay wf-expanded-overlay${expandedClosing ? " is-closing" : ""}`} role="presentation" onMouseDown={e=>e.target===e.currentTarget&&closeExpanded()}>
        <section className="ws-modal wf-expanded-modal" role="dialog" aria-modal="true" aria-label="Workflow complet">
          <header className="ws-modal-head"><div><div className="ws-eyebrow">VIQ.AI Workflow Orchestrator</div><h2>Workflow équipe</h2><p>À faire, en cours, à valider et validé — assignations et progression partagées.</p></div><button className="ws-icon-btn" onClick={closeExpanded}>✕</button></header>
          <div className="wf-expanded-body"><WorkflowBoard modalMode allowExpand={false} onOpenGoals={onOpenGoals} onOpenDossier={onOpenDossier} onOpenLead={onOpenLead} onOpenAgenda={onOpenAgenda} onOpenNote={onOpenNote} onCountChange={onCountChange}/></div>
        </section>
      </div>}
    </div>
  );
}
